import { Component, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule, ApexDataLabels, ChartComponent } from 'ng-apexcharts';
import {
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexChart,
  ApexStroke,
  ApexFill,
  ApexPlotOptions,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexLegend,
  ApexTooltip
} from 'ng-apexcharts';
import { CustomizeGuidelinesDTO, GuidelineDTO, StrategyDTO } from '../../models';
import { Observable, withLatestFrom, firstValueFrom } from 'rxjs';
import ApexCharts from 'apexcharts';
import { Store } from '@ngrx/store';
import {
  D4CActions, Selectors
} from '../../store';
import { CaSubscriber } from '@ca/ca-utils';
import { RouterModule } from '@angular/router';
import { KeyLabel, MultiSelectDropdownComponent } from '@ca/ca-ng-core';
import { ToggleGuidelineSelection } from '../../store/actions';
import { LanguagePrefixPipe } from "../../../../../../libs/ca-ng-core/src/lib/pipes/language-prefix.pipe";
import { selectAllGuidelines, selectTranslationsForGuidelines } from '../../store/selectors';
import { D4CAppState } from '../../store/models';
import { SERVER_BASE_URL } from '@ca/ca-ng-core';

@Component({
  selector: 'ca-customize-guidelines',
  imports: [
    CommonModule,
    NgApexchartsModule,
    RouterModule,
    MultiSelectDropdownComponent,
    LanguagePrefixPipe
  ],
  templateUrl: './customize-guidelines.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class CustomizeGuidelinesComponent implements OnInit, OnChanges, OnDestroy {
  public readonly base = inject(SERVER_BASE_URL);
  private sub = new CaSubscriber();
  private readonly host = inject(ElementRef<HTMLElement>);
  @Input() data!: CustomizeGuidelinesDTO;
  @ViewChild('strategySummary') protected strategySummaryRef!: ElementRef<HTMLUListElement>;

  protected categoryRatios: Record<string, { label: string, ratio: number }> = {};
  public polarChartOptions: BarChartOptions = createBasePolarChartOptions();
  protected barChartOptions: PieChartOptions = createBasePieChartOptions();
  private readonly store: Store<D4CAppState> = inject(Store);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected currentPage = 0;

  protected readonly filteredStrategies$: Observable<StrategyDTO[]> = this.store.select(Selectors.selectStrategiesWithFilteredGuidelines);

  protected readonly filters$ = this.store.select(s => ({
    categories: s.guidelines.categories,
    categoryOptions: s.guidelines.categories.map(o => ({ key: o.filter, label: o.name })),
    presetOptions: s.guidelines.presets.map(o => ({ key: o.filter, label: o.name })),
    selectedCategories: s.guidelines.filters.categories,
    selectedPresets: s.guidelines.filters.presets,
  }));
  protected readonly selectedRows$: Observable<Set<string>> = this.store.select(Selectors.selectGuidelinesSelectedRows);
  protected readonly areAllSelected$: Observable<boolean> = this.store.select(Selectors.selectAreAllGuidelinesSelected);
  protected readonly translations$ = this.store.select(selectTranslationsForGuidelines);
  private GuidelinesSelection$ = this.selectedRows$.pipe(withLatestFrom(this.store.select(selectAllGuidelines)));

  protected selectedRows: Set<string> = new Set();
  protected totalAmountOfGuidelines: number = 0;
  protected categoryOptions: KeyLabel[] = [];
  protected presetOptions: KeyLabel[] = [];
  protected selectedCategoryFilters: Set<string> = new Set();
  protected selectedPresetFilters: Set<string> = new Set();
  protected openAccordeon: string | null = null;
  protected guidelineCategories: CustomizeGuidelinesDTO['categories'] = [];
  private pendingRestore: { openStrategyId: string | null; focusGuidelineId: string | null } | null = null;
  private isGuidelineDetailPage = false;
  private hasInitializedListState = false;

  private guidelinesSelectionObserver: ([selected, allGuidelines]: [Set<string>, GuidelineDTO[]]) => void = ([selected, allGuidelines]) => {
    // calculate totals and percentages per category
    const allTotals = sumWeights(allGuidelines);
    const selectedGuidelines = allGuidelines.filter(g => selected.has(g.id));
    const selectedTotals = sumWeights(selectedGuidelines);
    this.totalAmountOfGuidelines = allGuidelines.length;

    const ratios: Record<string, { label: string, ratio: number }> = {};
    Object.keys(allTotals).forEach(key => {
      const label = this.guidelineCategories.find(l => l.filter === key);
      const totalAll = allTotals[key] ?? 0;
      const totalSel = selectedTotals[key] ?? 0;
      ratios[key] = { label: label?.name ?? key, ratio: totalAll > 0 ? (totalSel / totalAll) * 100 : 0 };
    });

    this.categoryRatios = ratios;
    const orderedCategories = this.guidelineCategories;
    const polarLabels = orderedCategories.map(category => category.name);
    const polarRatios = orderedCategories.map(category => ratios[category.filter]?.ratio ?? 0);
    this.polarChartOptions.series = [
      {
        name: 'Weight',
        data: polarRatios.map((value, index) => ({
          x: polarLabels[index],
          y: value,
          fillColor: palette[index % palette.length],
        }))
      },
      {
        name: 'Label anchor',
        data: polarRatios.map((value, index) => ({
          x: polarLabels[index],
          y: Math.max(0, 100 - value),
          fillColor: unfilledBarColor,
        }))
      }
    ];
    this.polarChartOptions.xaxis = {
      ...createBasePolarChartOptions().xaxis,
      categories: polarLabels
    };

    const barLabels = orderedCategories.map(c => c.name);
    const selectedWeights = orderedCategories.map(c => selectedTotals[c.filter] ?? 0);
    const totalSelectedWeight = selectedWeights.reduce((sum, value) => sum + value, 0);
    const barRatios = selectedWeights.map(value => totalSelectedWeight > 0 ? (value / totalSelectedWeight) * 100 : 0);

    this.barChartOptions.series = totalSelectedWeight > 0 ? barRatios : orderedCategories.map(() => 1);
    this.barChartOptions.labels = barLabels;
    this.barChartOptions.colors = totalSelectedWeight > 0 ? [...palette] : orderedCategories.map(() => emptyPieColor);
    this.barChartOptions.fill = {
      ...createBasePieChartOptions().fill,
      colors: totalSelectedWeight > 0 ? [...palette] : orderedCategories.map(() => emptyPieColor)
    };
    this.barChartOptions.stroke = {
      ...createBasePieChartOptions().stroke,
      colors: totalSelectedWeight > 0 ? [...palette] : orderedCategories.map(() => emptyPieColor)
    };
    this.barChartOptions.legend = {
      ...createBasePieChartOptions().legend,
      show: true
    };
    this.selectedRows = selected;
  };

  get selectionPercentage(): number {
    return this.totalAmountOfGuidelines > 0
      ? (this.selectedRows.size / this.totalAmountOfGuidelines) * 100
      : 0;
  }

  private filtersObserver = (
    {
      categoryOptions,
      presetOptions,
      selectedCategories,
      selectedPresets,
      categories,
    }: {
      categoryOptions: KeyLabel[],
      presetOptions: KeyLabel[],
      selectedCategories: string[],
      selectedPresets: string[],
      categories: CustomizeGuidelinesDTO['categories'],
    }
  ) => {
    this.categoryOptions = categoryOptions;
    this.presetOptions = presetOptions;
    this.selectedCategoryFilters = new Set(selectedCategories ?? []);
    this.selectedPresetFilters = new Set(selectedPresets ?? []);
    this.guidelineCategories = categories;
  };

  ngOnInit(): void {
    if (!this.data) throw new Error('no data!');

    // Skip all list setup when rendered on a guideline detail page.
    firstValueFrom(this.store.select(Selectors.selectCurrentRoute)).then(route => {
      this.isGuidelineDetailPage = Array.isArray(route) && route.some(segment =>
        segment === 'guideline' || segment.startsWith('guideline-')
      );
      if (this.isGuidelineDetailPage) return;

      this.initializeListState();
      this.loadGuidelinesData();

      // Attempt to restore UI state (open accordion + scroll target) after data renders
      firstValueFrom(this.store.select(Selectors.selectGuidelinesUi)).then(ui => {
        if (ui.openStrategyId || ui.focusGuidelineId) {
          this.pendingRestore = { openStrategyId: ui.openStrategyId, focusGuidelineId: ui.focusGuidelineId };
          if (ui.openStrategyId) this.openAccordeon = ui.openStrategyId;
          // Try restoration once strategies appear
          this.sub.subscribe(this.filteredStrategies$, () => this.tryRestoreOnce());
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || !this.data || !this.hasInitializedListState || this.isGuidelineDetailPage) {
      return;
    }

    this.loadGuidelinesData();
  }

  ngOnDestroy(): void {
    this.sub.closeSubscriptions();
  }

  isGuidelineSelected(guideline: GuidelineDTO): boolean {
    return this.selectedRows.has(guideline.id);
  }

  toggleGuideline(guideline: GuidelineDTO, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.store.dispatch(ToggleGuidelineSelection({ id: guideline.id }));
  }

  setMultiSelectFilter<K extends 'categories' | 'presets'>(
    filterType: K,
    selectedValues: Set<string>
  ): void {
    // console.log('selection changed event', filterType, selectedValues);
    this.store.dispatch(D4CActions.SetGuidelinesMultiSelectFilter({ filterType, selectedValues: Array.from(selectedValues) }));
  }

  resetFilters(): void {
    this.store.dispatch(D4CActions.ResetGuidelinesFilters());
  }

  async exportGuidelines() {
    const documentTitle = this.getExportTitle();
    const [graphBase64Png, secondGraphBase64Png] = await Promise.all([
      this.exportChartPngBase64('guidelinesPolar'),
      this.exportChartPngBase64('guidelinesPie'),
    ]);
    const strategySummary = this.exportStrategySummaries();
    const selection = [...this.selectedRows.values()];
    this.store.dispatch(D4CActions.ExportGuidelines({ documentTitle, selection, strategySummary, graphBase64Png, secondGraphBase64Png }));
  }

  toggleStrategy(_target: EventTarget | null, strategy: StrategyDTO): void {
    if (this.openAccordeon === strategy.id) {
      this.openAccordeon = null;
      this.store.dispatch(D4CActions.SetGuidelinesUi({ openStrategyId: null, focusGuidelineId: null }));
    } else {
      this.openAccordeon = strategy.id;
      this.store.dispatch(D4CActions.SetGuidelinesUi({ openStrategyId: strategy.id, focusGuidelineId: null }));

      // fallback if no previous panel or no transition detected
      setTimeout(() => {
        const el = document.getElementById(strategy.id);
        if (el) {
          this.scrollToVisibleTop(el, 'smooth');
        }
      }, 0);
    }
  }

  onReadMoreClick(strategy: StrategyDTO, guideline: GuidelineDTO): void {
    // Persist UI state so a return via breadcrumbs can restore
    const openId = this.openAccordeon ?? strategy.id;
    this.store.dispatch(D4CActions.SetGuidelinesUi({ openStrategyId: openId, focusGuidelineId: guideline.id }));
    // Ensure the upcoming detail page starts at top (helps when container scrolling is used)
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch { }
  }

  private initializeListState(): void {
    if (this.hasInitializedListState) return;

    this.hasInitializedListState = true;
    this.sub.subscribe(this.GuidelinesSelection$, this.guidelinesSelectionObserver);
    this.sub.subscribe(this.filters$, this.filtersObserver);
  }

  private loadGuidelinesData(): void {
    this.guidelineCategories = this.data.categories;
    this.store.dispatch(D4CActions.LoadGuidelines({
      strategies: this.data.strategies,
      categories: this.data.categories,
      presets: this.data.presets
    }));
  }

  private tryRestoreOnce(): void {
    if (!this.pendingRestore) return;
    const { openStrategyId, focusGuidelineId } = this.pendingRestore;
    // If an open strategy id was saved, ensure it's set
    if (openStrategyId) {
      this.openAccordeon = openStrategyId;
      // ensure binding applies before measuring/scrolling
      this.cdr.detectChanges();
    }
    const doScroll = () => {
      const anchorId = focusGuidelineId || openStrategyId;
      if (anchorId) {
        const el = document.getElementById(anchorId);
        if (el) {
          this.scrollToVisibleTop(el, 'auto');
        }
      }
      this.pendingRestore = null;
    };

    const panel = openStrategyId ? document.getElementById(openStrategyId)?.querySelector('.guidelines-table') as HTMLElement | null : null;
    if (panel) {
      let done = false;
      const onEnd = () => {
        if (done) return;
        done = true;
        panel.removeEventListener('transitionend', onEnd as any);
        setTimeout(doScroll, 0);
      };
      panel.addEventListener('transitionend', onEnd as any, { once: true } as any);
      setTimeout(() => { if (!done) onEnd(); }, 800);
    } else {
      setTimeout(doScroll, 0);
    }
  }

  getSelectedSummary(strategy: StrategyDTO): string {
    const selected = strategy.guidelines.filter(g => this.selectedRows.has(g.id)).length;
    const total = strategy.guidelines.length;
    return `${selected} / ${total}`;
  }

  private async exportChartPngBase64(chartId: string, scale: number = 3): Promise<string> {
    // Wait for webfonts so PNG text renders in neue-haas…
    const anyDoc: any = document;
    if (anyDoc?.fonts?.ready?.then) {
      try { await anyDoc.fonts.ready; } catch { }
    }

    const { imgURI } = await ApexCharts.exec(chartId, 'dataURI', {
      type: 'png',
      scale
    }); // imgURI: "data:image/png;base64,AAAA..."

    // Return raw base64 only
    return imgURI.split(',')[1] ?? '';
  }

  private exportStrategySummaries(): string[] {
    const summary: string[] = [];
    this.strategySummaryRef.nativeElement.childNodes.forEach(v => { if (v.textContent) summary.push(v.textContent) });
    return summary;
  }

  private getExportTitle(): string {
    const hostElement = this.host.nativeElement;
    const titleElement = this.findNearestPreviousHeading(hostElement);
    return titleElement?.textContent?.trim() || 'Guidelines';
  }

  private findNearestPreviousHeading(start: HTMLElement): HTMLHeadingElement | null {
    let current: HTMLElement | null = start;

    while (current) {
      let sibling = current.previousElementSibling as HTMLElement | null;
      while (sibling) {
        const heading = sibling.matches('h2') ? sibling as HTMLHeadingElement : sibling.querySelector('h2');
        if (heading?.textContent?.trim()) {
          return heading;
        }
        sibling = sibling.previousElementSibling as HTMLElement | null;
      }

      current = current.parentElement;
    }

    return document.querySelector('h2');
  }

  @ViewChild('apexChart', { static: false }) chart!: ChartComponent;

  isZoomed = false;

  toggleZoom() {
    this.isZoomed = !this.isZoomed;
  }

  private scrollToVisibleTop(element: HTMLElement, behavior: ScrollBehavior): void {
    const stickyOffset = this.getVisibleStickyOffset();
    const targetTop = window.scrollY + element.getBoundingClientRect().top - stickyOffset - 8;

    window.scrollTo({
      top: Math.max(0, targetTop),
      left: 0,
      behavior
    });
  }

  private getVisibleStickyOffset(): number {
    const stickyElements = ['filter-header', 'chart-container']
      .map(id => document.getElementById(id))
      .filter((element): element is HTMLElement => !!element);

    return stickyElements.reduce((maxOffset, element) => {
      const style = window.getComputedStyle(element);
      if (style.position !== 'sticky' && style.position !== 'fixed') {
        return maxOffset;
      }

      const rect = element.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
        return maxOffset;
      }

      const topOffset = Number.parseFloat(style.top || '0') || 0;
      const isPinned = style.position === 'fixed' || rect.top <= topOffset;
      if (!isPinned) {
        return maxOffset;
      }

      const visibleOffset = rect.bottom > topOffset ? rect.bottom : 0;
      return Math.max(maxOffset, visibleOffset);
    }, 0);
  }
}

// private
type BarChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  stroke: ApexStroke;
  fill: ApexFill;
  colors: string[];
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  plotOptions: ApexPlotOptions;
  tooltip: ApexTooltip;
};
type PieChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  stroke: ApexStroke;
  fill: ApexFill;
  colors: string[];
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  labels: string[];
  tooltip: ApexTooltip;
  plotOptions: ApexPlotOptions;
};
const palette = [
  '#38A6B5',
  '#7CC6B2',
  '#1F7A8C',
  '#F4A261',
  '#E76F51',
  '#E9C46A'
];

const labelColors = ['#333333', '#333333', '#333333', '#333333', '#333333', '#333333'];
const unfilledBarColor = '#e6e6e6';
const emptyPieColor = '#d9d9d9';

function wrapAxisLabel(value: string, maxLineLength: number = 12): string {
  const words = value.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length > maxLineLength && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }

    currentLine = nextLine;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.join('\n');
}

function getChartDataValue(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }

  if (value && typeof value === 'object' && 'y' in value) {
    return Number((value as { y?: unknown }).y ?? 0);
  }

  return 0;
}

function createBasePolarChartOptions(): BarChartOptions {
  return {
    chart: {
      type: 'bar',
      id: 'guidelinesPolar',
      height: 200,
      stacked: true,
      toolbar: { show: false },
      redrawOnParentResize: true,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        distributed: false,
        barHeight: '55%',
        dataLabels: { position: 'top' }
      },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [1],
      formatter: function (_val: number, opts?: { dataPointIndex: number; w: { config: { series: { data: unknown[] }[] } } }) {
        const actualValue = opts?.w.config.series?.[0]?.data?.[opts.dataPointIndex] ?? 0;
        return `${getChartDataValue(actualValue).toFixed(1)}%`;
      },
      offsetY: -3,
      offsetX: -8,
      textAnchor: 'end',
      style: {
        fontSize: "12px",
        colors: labelColors
      }
    },
    stroke: {
      show: false,
      width: 0,
      colors: ['transparent', 'transparent'],
      fill: { colors: ['#38A6B5', 'transparent'], opacity: 1 }
    },
    series: [
      { name: 'Weight', data: [] },
      { name: 'Label anchor', data: [] }
    ],
    responsive: [],
    xaxis: {
      categories: [
        'Design',
        'Material',
        'Production',
        'Logistic',
        'Use',
        'End of cycle/life'
      ],
      type: 'category',
      min: 0,
      max: 100,
      tickAmount: 5,
      decimalsInFloat: 0,
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      labels: {
        show: false,
        trim: false,
        formatter: (val: string | number) => `${Math.round(Number(val))}%`
      },
    },
    fill: {
      opacity: [1, 1],
      colors: ['#38A6B5', unfilledBarColor],
    },
    colors: ['#38A6B5', unfilledBarColor],
    yaxis: {
      show: true,
      showAlways: true,
      labels: {
        maxWidth: 180,
        formatter: (value: string | number) => wrapAxisLabel(String(value))
      }
    },
    legend: {
      show: false,
      fontSize: "12px"
    },
    tooltip: {
      enabled: true,
      enabledOnSeries: [0],
      y: {
        formatter: (value: number) => `${value.toFixed(1)} %`
      }
    }
  };
}

function createBasePieChartOptions(): PieChartOptions {
  return {
    chart: {
      type: 'donut',
      id: 'guidelinesPie',
      height: '100%',
      toolbar: { show: false },
      redrawOnParentResize: true,
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toFixed(1) + "%";
      },
      style: {
        fontSize: "10px",
        colors: ['#ffffff']
      },
      background: {
        enabled: false
      },
      dropShadow: {
        enabled: true,
        color: '#000000',
        opacity: 0.4,
        blur: 2,
        left: 0,
        top: 1
      }
    },
    plotOptions: {
      pie: {
        dataLabels: {
          offset: 0,
          minAngleToShowLabel: 0,
        },
        donut: {
          size: '55%',
        }
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: palette,
      fill: { colors: palette, opacity: 1 }
    },
    series: [],
    responsive: [],
    labels: [],
    fill: {
      opacity: 1,
      colors: palette,
    },
    colors: palette,
    legend: {
      show: true,
      position: 'right',
      fontSize: "12px"
    },
    tooltip: {
      y: {
        formatter: (value: number) => `${value.toFixed(1)} %`
      }
    }
  };
}

export function sumWeights(guidelines: GuidelineDTO[]): Record<string, number> {
  return guidelines.reduce((acc, guideline) => {
    Object.entries(guideline.categoryFilterWeightPoints).forEach(([key, value]) => {
      acc[key] = (acc[key] || 0) + value;
    });
    return acc;
  }, {} as Record<string, number>);
}
